import { create } from "zustand";
import type { PlayerProfile } from "@chessla/shared";
import { ApiRequestError } from "@/shared/api/client";
import { fetchMyPlayerProfile, updateMyPlayerProfile, type UpdatePlayerFields } from "./profile.api";

interface ProfileState {
  ownerUserId: string | null;
  profile: PlayerProfile | null;
  notOnboarded: boolean;
  isLoading: boolean;
  savedAt: number | null;
  replaceProfile: (profile: PlayerProfile) => void;
  loadProfile: (userId: string | null) => Promise<void>;
  refreshProfile: () => Promise<void>;
  optimisticallyUpdateProfile: (fields: UpdatePlayerFields) => void;
  updateProfile: (fields: UpdatePlayerFields) => Promise<void>;
}

const inFlightRequests = new Map<string, Promise<void>>();
const pendingUpdates = new Map<string, Promise<void>>();
let profileRevision = 0;

const requestProfile = (set: (state: Partial<ProfileState>) => void, get: () => ProfileState, userId: string) => {
  const existing = inFlightRequests.get(userId);
  if (existing) return existing;
  const revision = profileRevision;

  const request = fetchMyPlayerProfile()
    .then((profile) => {
      if (get().ownerUserId === userId && revision === profileRevision)
        set({ profile, notOnboarded: false, isLoading: false });
    })
    .catch((error: unknown) => {
      if (error instanceof ApiRequestError && error.status === 404) {
        if (get().ownerUserId === userId && revision === profileRevision)
          set({ profile: null, notOnboarded: true, isLoading: false });
        return;
      }
      if (get().ownerUserId === userId && revision === profileRevision) set({ isLoading: false });
      throw error;
    })
    .finally(() => {
      if (inFlightRequests.get(userId) === request) inFlightRequests.delete(userId);
    });

  inFlightRequests.set(userId, request);
  return request;
};

export const useProfileStore = create<ProfileState>((set, get) => ({
  ownerUserId: null,
  profile: null,
  notOnboarded: false,
  isLoading: true,
  savedAt: null,
  replaceProfile: (profile) => {
    profileRevision += 1;
    inFlightRequests.clear();
    pendingUpdates.clear();
    set({ profile, notOnboarded: false, isLoading: false, savedAt: null });
  },
  loadProfile: async (userId) => {
    if (!userId) {
      set({ ownerUserId: null, profile: null, notOnboarded: false, isLoading: false, savedAt: null });
      return;
    }
    if (get().ownerUserId !== userId) {
      set({ ownerUserId: userId, profile: null, notOnboarded: false, isLoading: true, savedAt: null });
    } else if (get().profile !== null || get().notOnboarded) return;
    return requestProfile(set, get, userId);
  },
  refreshProfile: async () => {
    const userId = get().ownerUserId;
    if (!userId) return;
    return requestProfile(set, get, userId);
  },
  optimisticallyUpdateProfile: (fields) => {
    set((state) => ({ profile: state.profile ? { ...state.profile, ...fields } : null, savedAt: null }));
  },
  updateProfile: async (fields) => {
    const ownerUserId = get().ownerUserId;
    if (!ownerUserId) return;
    const revision = profileRevision;
    const previous = pendingUpdates.get(ownerUserId);
    const request = Promise.resolve(previous)
      .catch(() => undefined)
      .then(async () => {
        if (get().ownerUserId !== ownerUserId || revision !== profileRevision) return;
        const profile = await updateMyPlayerProfile(fields);
        if (
          get().ownerUserId === ownerUserId &&
          revision === profileRevision &&
          pendingUpdates.get(ownerUserId) === request
        )
          set({ profile, savedAt: Date.now() });
      });
    pendingUpdates.set(ownerUserId, request);
    try {
      await request;
    } finally {
      if (pendingUpdates.get(ownerUserId) === request) pendingUpdates.delete(ownerUserId);
    }
  },
}));
